import { useState, useRef, useEffect, useMemo, useCallback } from "react"
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core"
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { MdDelete, MdVisibility, MdVisibilityOff, MdLayers, MdLock, MdLockOpen } from "react-icons/md"
import { GripVertical } from "lucide-react"
import { Slider } from "@/components/ui/shadcn/slider"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/shadcn/select"
import Button from "@/components/ui/Button"
import { normalizeLayerFilters, LAYER_BLEND_MODES, DEFAULT_LAYER_FILTERS } from "./layerFilters"

const LAYER_SLIDER_ROOT = "w-full h-3"
const LAYER_SLIDER_TRACK = "h-1.5"
const LAYER_SLIDER_THUMB = "h-2 w-2 border-0 shadow-sm"

/** Compact slider row; double-click the track area resets to default. */
function LayerSliderField({ label, valueDisplay, onReset, children }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-secondary-foreground/60 w-14 shrink-0 leading-tight" title={label}>
                {label}
            </span>
            <div
                className="flex-1 min-w-0 h-3 flex items-center"
                onDoubleClick={(e) => {
                    e.preventDefault()
                    onReset()
                }}
                title="Double-click to reset"
            >
                {children}
            </div>
            <span className="text-[10px] text-secondary-foreground/60 w-7 text-right tabular-nums shrink-0 leading-tight">
                {valueDisplay}
            </span>
        </div>
    )
}

function SortableLayerRow({
    layer,
    activeLayerId,
    editingId,
    editValue,
    setEditValue,
    inputRef,
    layersLength,
    onSelectLayer,
    onToggleLock,
    onToggleVisibility,
    onRemoveLayer,
    startRename,
    commitRename,
    setEditingId,
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: layer._id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    const isActive = layer._id === activeLayerId
    const isEditing = editingId === layer._id

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-1 px-2 py-2 border-b transition-colors ${
                isDragging ? 'bg-secondary/80 shadow-md z-10' : ''
            } ${isActive ? 'bg-secondary' : 'hover:bg-secondary/50'}`}
        >
            <button
                type="button"
                className="shrink-0 p-1 rounded touch-none cursor-grab active:cursor-grabbing text-secondary-foreground/40 hover:text-secondary-foreground hover:bg-background/50 outline-none focus-visible:ring-1 focus-visible:ring-ring"
                aria-label="Drag to reorder layer"
                {...attributes}
                {...listeners}
            >
                <GripVertical className="w-4 h-4" />
            </button>

            <div
                role="button"
                tabIndex={0}
                className="flex flex-1 min-w-0 items-center gap-1 cursor-pointer"
                onClick={() => onSelectLayer(layer._id)}
                onKeyDown={(e) => {
                    if (e.target.tagName === 'INPUT') return
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSelectLayer(layer._id)
                    }
                }}
            >
                <div className="flex items-center justify-center w-5 h-5 shrink-0">
                    <MdLayers className="w-3 h-3 text-secondary-foreground/60" />
                </div>

                <div className="flex-1 min-w-0">
                    {isEditing ? (
                        <input
                            ref={inputRef}
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={commitRename}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commitRename()
                                if (e.key === 'Escape') setEditingId(null)
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-secondary-foreground bg-background/80 border border-primary rounded px-1 py-0.5 w-full outline-none"
                        />
                    ) : (
                        <span
                            className="text-xs text-secondary-foreground truncate block"
                            onDoubleClick={(e) => { e.stopPropagation(); startRename(layer) }}
                        >
                            {layer.name}
                        </span>
                    )}
                </div>
            </div>

            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleLock(layer._id) }}
                className="p-1 rounded hover:bg-background/50 text-secondary-foreground/60 cursor-pointer shrink-0"
                title={layer.locked ? "Unlock layer" : "Lock layer"}
            >
                {layer.locked
                    ? <MdLock className="w-3.5 h-3.5" />
                    : <MdLockOpen className="w-3.5 h-3.5 opacity-40" />
                }
            </button>

            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleVisibility(layer._id) }}
                className="p-1 rounded hover:bg-background/50 text-secondary-foreground/60 cursor-pointer shrink-0"
            >
                {layer.visible
                    ? <MdVisibility className="w-3.5 h-3.5" />
                    : <MdVisibilityOff className="w-3.5 h-3.5 opacity-40" />
                }
            </button>

            {layersLength > 1 && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemoveLayer(layer._id) }}
                    className="p-1 rounded hover:bg-destructive/20 text-secondary-foreground/40 hover:text-destructive cursor-pointer shrink-0"
                >
                    <MdDelete className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    )
}

export default function LayerPanel({
    layers,
    activeLayerId,
    onSelectLayer,
    onAddLayer,
    onRemoveLayer,
    onToggleVisibility,
    onToggleLock,
    onChangeOpacity,
    onCommitOpacity,
    onChangeLayerFilter,
    onCommitLayerFilters,
    onChangeBlendMode,
    onReorderLayers,
    onRenameLayer,
}) {
    const sortedLayers = useMemo(() => [...layers].sort((a, b) => b.order - a.order), [layers])
    const sortableIds = useMemo(() => sortedLayers.map(l => l._id), [sortedLayers])

    const activeLayer = useMemo(
        () => (activeLayerId ? layers.find(l => l._id === activeLayerId) : null),
        [layers, activeLayerId]
    )
    const f = activeLayer ? normalizeLayerFilters(activeLayer) : null

    const [editingId, setEditingId] = useState(null)
    const [editValue, setEditValue] = useState('')
    const inputRef = useRef(null)

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event
        if (!over || active.id === over.id) return
        const oldIndex = sortableIds.indexOf(active.id)
        const newIndex = sortableIds.indexOf(over.id)
        if (oldIndex === -1 || newIndex === -1) return
        const next = arrayMove(sortableIds, oldIndex, newIndex)
        onReorderLayers?.(next)
    }, [sortableIds, onReorderLayers])

    useEffect(() => {
        if (editingId && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [editingId])

    const startRename = (layer) => {
        setEditingId(layer._id)
        setEditValue(layer.name)
    }

    const commitRename = () => {
        if (editingId && editValue.trim()) {
            onRenameLayer?.(editingId, editValue.trim())
        }
        setEditingId(null)
    }

    return (
        <div className="flex flex-col h-full bg-background border-l w-80 min-h-0">
            <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
                <span className="text-xs font-semibold text-secondary-foreground uppercase tracking-wider">Layers</span>
                <Button type="secondary" size="sm" icon={<MdLayers className="w-3.5 h-3.5" />} onClick={onAddLayer} title="Add layer">
                    New Layer
                </Button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                        {sortedLayers.map((layer) => (
                            <SortableLayerRow
                                key={layer._id}
                                layer={layer}
                                activeLayerId={activeLayerId}
                                editingId={editingId}
                                editValue={editValue}
                                setEditValue={setEditValue}
                                inputRef={inputRef}
                                layersLength={layers.length}
                                onSelectLayer={onSelectLayer}
                                onToggleLock={onToggleLock}
                                onToggleVisibility={onToggleVisibility}
                                onRemoveLayer={onRemoveLayer}
                                startRename={startRename}
                                commitRename={commitRename}
                                setEditingId={setEditingId}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>

            <div className="border-t shrink-0 bg-secondary/20">
                <div className="px-3 py-2 border-b border-border/60">
                    <span className="text-[10px] font-semibold text-secondary-foreground uppercase tracking-wider">
                        Layer properties
                    </span>
                    {activeLayer && (
                        <p className="text-xs text-secondary-foreground/70 truncate mt-0.5" title={activeLayer.name}>
                            {activeLayer.name}
                        </p>
                    )}
                </div>

                {activeLayer && f ? (
                    <div className="flex flex-col gap-2 px-3 py-3 max-h-[min(52vh,420px)] overflow-y-auto">
                         <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-secondary-foreground/60 w-14 shrink-0 leading-tight">Blending</span>
                            <Select
                                value={f.blendMode}
                                onValueChange={(v) => onChangeBlendMode?.(activeLayer._id, v)}
                            >
                                <SelectTrigger size="xs" className="w-full text-[10px] bg-transparent border-secondary-foreground/20 hover:border-secondary-foreground/40 py-1 px-2">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {LAYER_BLEND_MODES.map((m) => (
                                        <SelectItem key={m.value} value={m.value} className="text-xs">
                                            {m.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <LayerSliderField
                            label="Opacity"
                            valueDisplay={`${Math.round(activeLayer.opacity * 100)}%`}
                            onReset={() => {
                                onChangeOpacity(activeLayer._id, 1)
                                onCommitOpacity?.()
                            }}
                        >
                            <Slider
                                value={[activeLayer.opacity * 100]}
                                min={0}
                                max={100}
                                step={1}
                                onValueChange={([v]) => onChangeOpacity(activeLayer._id, v / 100)}
                                onValueCommit={() => onCommitOpacity?.()}
                                className={LAYER_SLIDER_ROOT}
                                trackClassName={LAYER_SLIDER_TRACK}
                                thumbClassName={LAYER_SLIDER_THUMB}
                            />
                        </LayerSliderField>



                        <div className="h-px bg-border/60 my-0.5" aria-hidden />

                        <LayerSliderField
                            label="Blur"
                            valueDisplay={f.blur.toFixed(1)}
                            onReset={() => {
                                onChangeLayerFilter?.(activeLayer._id, { blur: DEFAULT_LAYER_FILTERS.blur })
                                onCommitLayerFilters?.()
                            }}
                        >
                            <Slider
                                value={[f.blur]}
                                min={0}
                                max={24}
                                step={0.5}
                                onValueChange={([v]) => onChangeLayerFilter?.(activeLayer._id, { blur: v })}
                                onValueCommit={() => onCommitLayerFilters?.()}
                                className={LAYER_SLIDER_ROOT}
                                trackClassName={LAYER_SLIDER_TRACK}
                                thumbClassName={LAYER_SLIDER_THUMB}
                            />
                        </LayerSliderField>

                        <LayerSliderField
                            label="Saturation"
                            valueDisplay={`${Math.round(f.saturation * 100)}%`}
                            onReset={() => {
                                onChangeLayerFilter?.(activeLayer._id, { saturation: DEFAULT_LAYER_FILTERS.saturation })
                                onCommitLayerFilters?.()
                            }}
                        >
                            <Slider
                                value={[f.saturation * 100]}
                                min={0}
                                max={200}
                                step={1}
                                onValueChange={([v]) => onChangeLayerFilter?.(activeLayer._id, { saturation: v / 100 })}
                                onValueCommit={() => onCommitLayerFilters?.()}
                                className={LAYER_SLIDER_ROOT}
                                trackClassName={LAYER_SLIDER_TRACK}
                                thumbClassName={LAYER_SLIDER_THUMB}
                            />
                        </LayerSliderField>

                        <LayerSliderField
                            label="Brightness"
                            valueDisplay={`${Math.round(f.brightness * 100)}%`}
                            onReset={() => {
                                onChangeLayerFilter?.(activeLayer._id, { brightness: DEFAULT_LAYER_FILTERS.brightness })
                                onCommitLayerFilters?.()
                            }}
                        >
                            <Slider
                                value={[f.brightness * 100]}
                                min={0}
                                max={200}
                                step={1}
                                onValueChange={([v]) => onChangeLayerFilter?.(activeLayer._id, { brightness: v / 100 })}
                                onValueCommit={() => onCommitLayerFilters?.()}
                                className={LAYER_SLIDER_ROOT}
                                trackClassName={LAYER_SLIDER_TRACK}
                                thumbClassName={LAYER_SLIDER_THUMB}
                            />
                        </LayerSliderField>

                        <LayerSliderField
                            label="Contrast"
                            valueDisplay={`${Math.round(f.contrast * 100)}%`}
                            onReset={() => {
                                onChangeLayerFilter?.(activeLayer._id, { contrast: DEFAULT_LAYER_FILTERS.contrast })
                                onCommitLayerFilters?.()
                            }}
                        >
                            <Slider
                                value={[f.contrast * 100]}
                                min={0}
                                max={200}
                                step={1}
                                onValueChange={([v]) => onChangeLayerFilter?.(activeLayer._id, { contrast: v / 100 })}
                                onValueCommit={() => onCommitLayerFilters?.()}
                                className={LAYER_SLIDER_ROOT}
                                trackClassName={LAYER_SLIDER_TRACK}
                                thumbClassName={LAYER_SLIDER_THUMB}
                            />
                        </LayerSliderField>
                    </div>
                ) : (
                    <div className="px-3 py-6 text-center text-xs text-secondary-foreground/50">
                        Select a layer to edit opacity, blend, and filters.
                    </div>
                )}
            </div>
        </div>
    )
}
